import { useEffect, useState } from "react";
import papi, { formatErr } from "@/lib/partnerApi";
import { SectionHead } from "./VdaShared";
import { Button } from "@/components/ui/button";
import { Download, Loader2, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["", "Discovered", "Verified", "Contacted", "Onboarded", "Rejected"];

export default function VdaExport() {
  const [status, setStatus] = useState("");
  const [count, setCount] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyX, setBusyX] = useState(false);

  useEffect(() => {
    const params = status ? { status } : {};
    papi.get("/vda/prospects", { params }).then((r) => setCount(r.data.length)).catch(() => setCount(0));
  }, [status]);

  const download = async () => {
    setBusy(true);
    try {
      const { data } = await papi.get("/vda/export.csv", { params: status ? { status } : {}, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `vendor_prospects${status ? "_" + status.toLowerCase() : ""}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const downloadXlsx = async () => {
    setBusyX(true);
    try {
      const { data } = await papi.get("/vda/export.xlsx", { params: status ? { status } : {}, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url; a.download = `vendor_prospects${status ? "_" + status.toLowerCase() : ""}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusyX(false); }
  };

  return (
    <div className="max-w-2xl">
      <SectionHead title="Export" desc="Download vendor prospects as CSV or Excel for onboarding or outreach." />
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-500/10"><FileSpreadsheet className="h-7 w-7 text-emerald-600" /></div>
        <h3 className="mt-4 font-display text-lg font-bold text-slate-900">Export prospects</h3>
        <div className="mt-4">
          <label className="text-xs font-medium uppercase tracking-wide text-slate-400">Filter by status</label>
          <select data-testid="export-status" className="mt-1.5 block h-10 w-64 rounded-xl border border-slate-200 bg-white px-3 text-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s} value={s}>{s || "All statuses"}</option>)}
          </select>
        </div>
        <p className="mt-4 text-sm text-slate-500">{count === null ? "Counting…" : <><span className="font-bold text-slate-800">{count}</span> prospect{count === 1 ? "" : "s"} will be exported.</>}</p>
        <div className="mt-5 flex flex-wrap gap-3">
          <Button data-testid="export-download" onClick={download} disabled={busy || !count} variant="outline" className="h-11 rounded-xl border-slate-300 px-6">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />} Download CSV
          </Button>
          <Button data-testid="export-download-xlsx" onClick={downloadXlsx} disabled={busyX || !count} className="h-11 rounded-xl bg-emerald-600 px-6 hover:bg-emerald-700">
            {busyX ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />} Download Excel (.xlsx)
          </Button>
        </div>
      </div>
    </div>
  );
}
